import { useSuspenseQueries, useSuspenseQuery } from '@tanstack/react-query';
import { MutableRefObject } from 'react';
import { MapRef } from 'react-map-gl';

import { getRequest } from '@/api/rest/hooks/requests/get';
import { MAP_LAYER_FACTS } from '@/api/rest/resources/types/fact';
import {
  GET_GEOSERVER_LAYER_FEATURES,
  GetGeoserverLayerFeaturesErrorDto,
  GetGeoserverLayerFeaturesSuccessDto,
} from '@/api/rest/resources/types/geoserver';
import { LAYER_OPTIONS, MAP_ORIGIN } from '@/components/widgets/o/Map/constants';
import { IUCN_STATIC_HASH } from '@/config/constants';
import { usePlotFacts } from '@/pages/shared/hooks/usePlotFact';
import { getFactFromFactArray } from '@/pages/shared/hooks/utils';
import { assertIsDefined } from '@/utils/assert';

const DEFAULT_QUERY_PARAMS = {
  service: 'WMS',
  version: '1.1.1',
  request: 'GetFeatureInfo',
  format: 'image/png',
  transparent: 'true',
  info_format: 'application/json',
  srs: 'EPSG:4326',
};

type SuccessDto = GetGeoserverLayerFeaturesSuccessDto;
type ErrorDto = GetGeoserverLayerFeaturesErrorDto;

type GetFeatureInfoBaseProps = {
  x: number;
  y: number;
  mapRef: MutableRefObject<MapRef | null>;
  queryParamsArg?: Partial<typeof DEFAULT_QUERY_PARAMS>;
  year: string;
};

type UseGetFeatureInfoProps = {
  layer: string;
} & GetFeatureInfoBaseProps;

export const useGetFeatureInfo = ({
  layer,
  x,
  y,
  mapRef,
  year,
  queryParamsArg = DEFAULT_QUERY_PARAMS,
}: UseGetFeatureInfoProps) => {
  const layerInfo = LAYER_OPTIONS.find((option) => option.value === layer);
  const canvas = mapRef.current?.getCanvas();
  const bounds = mapRef.current?.getBounds();

  assertIsDefined(layerInfo, 'layerInfo');
  assertIsDefined(canvas, 'canvas');
  assertIsDefined(bounds, 'bounds');

  const bbox = [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()].join(',');

  let layerName = layerInfo.isTimeSeries
    ? `${layerInfo.origin}:${layerInfo.value}_${year}`
    : `${layerInfo.origin}:${layerInfo.value}`;

  // SUPER HACK: to make iucn url unguessable. to be removed when keycloak is setup on geoserver
  if (layerInfo.value === MAP_LAYER_FACTS.iucn_protected_areas) {
    layerName = `${layerName}_${IUCN_STATIC_HASH}`;
  }

  const queryParams = {
    ...queryParamsArg,
    query_layers: layerName,
    layers: layerName,
    x: Math.round(x),
    y: Math.round(y),
    width: canvas.width,
    height: canvas.height,
    bbox,
  };

  const queryFn = async () => {
    return getRequest<SuccessDto>(GET_GEOSERVER_LAYER_FEATURES, undefined, queryParams);
  };

  return useSuspenseQuery<SuccessDto, ErrorDto>({
    queryKey: ['geoserver', 'map', queryParams],
    queryFn,
    staleTime: 1000 * 60 * 5,
  });
};

type UseGetFeatureInfoFromCombinedLayersProps = {
  layers: string[];
} & GetFeatureInfoBaseProps;

export const useGetFeatureInfoFromCombinedLayers = ({
  layers,
  x,
  y,
  mapRef,
  year,
  queryParamsArg = DEFAULT_QUERY_PARAMS,
}: UseGetFeatureInfoFromCombinedLayersProps) => {
  const canvas = mapRef.current?.getCanvas();
  const bounds = mapRef.current?.getBounds();

  const layersData = useGetLayerQueryNames(layers, year);

  assertIsDefined(layersData, 'layersData');
  assertIsDefined(canvas, 'canvas');
  assertIsDefined(bounds, 'bounds');

  const bbox = [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()].join(',');

  const baseQueryParams = {
    ...queryParamsArg,
    x: Math.round(x),
    y: Math.round(y),
    width: canvas.width,
    height: canvas.height,
    bbox,
  };

  return useSuspenseQueries({
    queries: layersData.map((layer) => {
      const queryParams = {
        ...baseQueryParams,
        query_layers: layer,
        layers: layer,
      };

      return {
        queryKey: ['geoserver', 'map', queryParams],
        queryFn: async () => {
          return getRequest<SuccessDto, ErrorDto>(GET_GEOSERVER_LAYER_FEATURES, undefined, queryParams);
        },
        staleTime: 1000 * 60 * 5,
      };
    }),
  });
};

const useGetLayerQueryNames = (layers: string[], year: string) => {
  const layersInfo = LAYER_OPTIONS.filter((option) => layers.includes(option.value));

  const factLayers = layersInfo.filter((l) => l.origin === MAP_ORIGIN.fact).map((l) => l.value) || [
    MAP_LAYER_FACTS.unknown,
  ];
  const facts = usePlotFacts(factLayers);

  return layersInfo
    .map((layer) => {
      if (layer.origin !== MAP_ORIGIN.fact) {
        return layer.isTimeSeries ? `${layer.origin}:${layer.value}_${year}` : `${layer.origin}:${layer.value}`;
      }

      const fact = getFactFromFactArray(facts, layer.value);
      const factValue = layer.isTimeSeries
        ? (fact?.value as Record<string, string>)?.[`${year}`]
        : (fact?.value as string);
      const layerName = factValue?.split('layers=')?.[1];

      return layerName ?? null;
    })
    .filter(Boolean);
};
